import {
	Fragment,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import message from '~/assets/icons/message.svg';
import refresh from '~/assets/icons/refresh.svg';
import trash from '~/assets/icons/trash.svg';
import {
	Button,
	ButtonBorderRadius,
	ButtonColors,
	ButtonHeight,
} from '~/components/ui/Button';
import { ImgIcon } from '~/components/ui/ImgIcon';
import { Popup } from '~/components/ui/Popup';
import { Text } from '~/components/ui/Text';
import { useZoomFactor } from '~/hooks';
import { rage, useObservable } from '~/rage';
import type { TransformedAdItem } from '~/rage/services/AdsService';
import { formatTime, PhoneApps, Role } from '~/shared';
import { MessengerStages } from '../../models/Messenger';
import { setActiveMessengerState, useLayoutDispatch } from '../../redux';
import { setActiveApp } from '../../redux/slices/phoneSlice';
import {
	StyledAdsTooltipPortal,
	StyledBottomContainer,
	StyledButtonContent,
	StyledButtonsContainer,
	StyledContainer,
	StyledMyAdsButtonsContainer,
	StyledStatusContainer,
	StyledText,
	StyledTopContainer,
} from './AppAdsItem.styled';

type AdsTooltipKind = 'delete-my' | 'renew' | 'delete-feed';

const getScrollableAncestors = (start: HTMLElement | null): HTMLElement[] => {
	const list: HTMLElement[] = [];
	let el = start?.parentElement ?? null;
	while (el) {
		const { overflow, overflowX, overflowY } = getComputedStyle(el);
		const combined = `${overflow} ${overflowX} ${overflowY}`;
		if (/\b(auto|scroll|overlay)\b/.test(combined)) {
			list.push(el);
		}
		el = el.parentElement;
	}
	return list;
};

const AppAdsItem = ({
	item,
	isMyAds = false,
}: {
	item: TransformedAdItem;
	isMyAds?: boolean;
}) => {
	const dispatch = useLayoutDispatch();
	const zoomFactor = useZoomFactor();
	const [isExpanded, setIsExpanded] = useState(false);
	const [isExpandable, setIsExpandable] = useState(false);
	const [adsTooltipOpen, setAdsTooltipOpen] = useState(false);
	const [adsTooltipKind, setAdsTooltipKind] =
		useState<AdsTooltipKind>('delete-my');
	const [adsTooltipPosition, setAdsTooltipPosition] = useState({
		top: 0,
		left: 0,
	});
	const textRef = useRef<HTMLDivElement>(null);
	const adsTooltipPopupRef = useRef<HTMLDivElement>(null);
	const deleteMyAdsAnchorRef = useRef<HTMLSpanElement>(null);
	const renewTooltipAnchorRef = useRef<HTMLSpanElement>(null);
	const deleteFeedAnchorRef = useRef<HTMLSpanElement>(null);

	useLayoutEffect(() => {
		setIsExpanded(false);
	}, [item.text]);

	useEffect(() => {
		const el = textRef.current;
		if (!el) return;

		const measure = () => {
			if (isExpanded) return;
			const overflow = el.scrollHeight > el.clientHeight + 1;
			setIsExpandable(overflow);
		};

		measure();
		const ro = new ResizeObserver(measure);
		ro.observe(el);
		return () => ro.disconnect();
	}, [item.text, isExpanded]);

	const dialogs = useObservable(rage.messages.getDialogs());
	const isStaff = rage.accessControl.hasMinimumRole(Role.MODERATOR);

	const canCall = item.callsAvailable && !item.isOwner;

	const handleDelete = useCallback(() => {
		rage.ads
			.deleteAd(item.id)
			.catch((err) => rage.notifications.showException(err));
	}, [item.id, item.isOwner]);

	const handleRenew = () => {
		rage.ads
			.renewAd(item.id)
			.catch((err) => rage.notifications.showException(err));
	};

	// пока не понятно, будет ли использоваться, оставим на будущее
	// const handleCall = () => {
	// 	rage.phoneCalls
	// 		.call(item.phoneNumber)
	// 		.catch((err) => rage.notifications.showException(err));
	// };

	const handleMessage = async () => {
		if (!item.callsAvailable) return;

		const existingDialog = dialogs.find(
			(d) => d.peer.phoneNumber === item.phoneNumber,
		);

		if (existingDialog) {
			rage.messages.setActiveDialogId(existingDialog.id);
		} else {
			try {
				const dialogId = await rage.messages.createDialog(item.phoneNumber, '');
				rage.messages.setActiveDialogId(dialogId);
			} catch (error) {
				rage.notifications.showException(error);
				return;
			}
		}

		dispatch(setActiveApp(PhoneApps.MESSENGER));
		dispatch(setActiveMessengerState(MessengerStages.DIALOG));
	};

	const handleToggleExpanded = () => {
		if (!isExpandable) return;
		setIsExpanded((prev) => !prev);
	};

	const showExpandedText = isExpandable && isExpanded;

	const adsTooltipText =
		adsTooltipKind === 'renew' ? 'Выложить\nзаново' : 'Удалить';

	const updateAdsTooltipPosition = useCallback(() => {
		const anchorEl =
			adsTooltipKind === 'delete-my'
				? deleteMyAdsAnchorRef.current
				: adsTooltipKind === 'renew'
				? renewTooltipAnchorRef.current
				: deleteFeedAnchorRef.current;
		if (!anchorEl) return;

		const z = zoomFactor || 1;
		const rect = anchorEl.getBoundingClientRect();
		const phoneEl = anchorEl.closest('[data-phone-screen]');
		const phoneRect = phoneEl?.getBoundingClientRect();

		const margin = 8;
		const gap = 8;

		const boundTop = (phoneRect?.top ?? 0) / z;
		const boundBottom = (phoneRect?.bottom ?? window.innerHeight) / z;
		const boundLeft = (phoneRect?.left ?? 0) / z;
		const boundRight = (phoneRect?.right ?? window.innerWidth) / z;

		const anchorCenterX = (rect.left + rect.width / 2) / z;
		const anchorTop = rect.top / z;
		const anchorBottom = rect.bottom / z;

		const popupBox = adsTooltipPopupRef.current?.getBoundingClientRect();
		const popupHeight =
			popupBox != null && popupBox.height > 0 ? popupBox.height / z : 52;
		const popupHalfWidth =
			popupBox != null && popupBox.width > 0 ? popupBox.width / 2 / z : 155;

		let top = anchorTop - popupHeight - gap;
		if (top < boundTop + margin) {
			top = anchorBottom + gap;
		}

		top = Math.max(
			boundTop + margin,
			Math.min(top, boundBottom - popupHeight - margin),
		);

		let left = anchorCenterX;
		left = Math.max(
			boundLeft + popupHalfWidth + margin,
			Math.min(left, boundRight - popupHalfWidth - margin),
		);

		setAdsTooltipPosition({ top, left });
	}, [adsTooltipKind, zoomFactor]);

	useLayoutEffect(() => {
		if (!adsTooltipOpen) return;

		const anchorEl =
			adsTooltipKind === 'delete-my'
				? deleteMyAdsAnchorRef.current
				: adsTooltipKind === 'renew'
				? renewTooltipAnchorRef.current
				: deleteFeedAnchorRef.current;
		if (!anchorEl) return;

		const handleUpdate = () => {
			updateAdsTooltipPosition();
		};

		handleUpdate();
		const rafId = requestAnimationFrame(handleUpdate);

		const scrollRoots = getScrollableAncestors(anchorEl);
		scrollRoots.forEach((el) =>
			el.addEventListener('scroll', handleUpdate, { passive: true }),
		);
		window.addEventListener('scroll', handleUpdate, true);
		window.addEventListener('resize', handleUpdate);

		return () => {
			cancelAnimationFrame(rafId);
			scrollRoots.forEach((el) =>
				el.removeEventListener('scroll', handleUpdate),
			);
			window.removeEventListener('scroll', handleUpdate, true);
			window.removeEventListener('resize', handleUpdate);
		};
	}, [adsTooltipOpen, adsTooltipKind, updateAdsTooltipPosition]);

	return (
		<Fragment>
			{createPortal(
				<CSSTransition
					nodeRef={adsTooltipPopupRef}
					in={adsTooltipOpen}
					classNames={'popup'}
					timeout={500}
					unmountOnExit
				>
					<StyledAdsTooltipPortal
						ref={adsTooltipPopupRef}
						style={{
							top: adsTooltipPosition.top,
							left: adsTooltipPosition.left,
						}}
					>
						<Popup
							text={adsTooltipText}
							style={{ opacity: 1, textAlign: 'center' }}
						/>
					</StyledAdsTooltipPortal>
				</CSSTransition>,
				document.body,
			)}
			<StyledContainer
				$expandable={isExpandable}
				onClick={handleToggleExpanded}
			>
				<StyledTopContainer>
					<Text style={{ fontSize: 14, color: 'rgba(255, 255, 255, 0.25)' }}>
						Автор:{' '}
						<span style={{ color: '#ffffff', fontWeight: 500 }}>
							{item.author}
						</span>
					</Text>
					<Text style={{ fontSize: 14, color: 'rgba(255, 255, 255, 0.25)' }}>
						{formatTime(item.time, '{hh}:{mm}', { useMoscowTimezone: true })}
					</Text>
				</StyledTopContainer>
				<StyledText ref={textRef} $isExpanded={showExpandedText}>
					{item.text}
				</StyledText>
				<StyledBottomContainer
					style={{ marginTop: showExpandedText ? '16px' : '0' }}
				>
					{isMyAds ? (
						<Fragment>
							<StyledStatusContainer $status={item.status}>
								<div className="dot" />
								<Text>
									{item.status === 'pending' ? 'В очереди' : 'Опубликовано'}
								</Text>
							</StyledStatusContainer>
							<StyledMyAdsButtonsContainer>
								<span
									ref={deleteMyAdsAnchorRef}
									style={{ display: 'inline-flex' }}
									onMouseEnter={() => {
										setAdsTooltipKind('delete-my');
										setAdsTooltipOpen(true);
									}}
									onMouseLeave={() => setAdsTooltipOpen(false)}
								>
									<Button
										color={ButtonColors.Cancel25}
										height={ButtonHeight.Height30}
										borderRadius={ButtonBorderRadius.Radius5}
										square={true}
										disabled={item.status === 'published'}
										style={{
											padding: '0 10px',
											cursor:
												item.status === 'published' ? 'default' : 'pointer',
										}}
										onClick={(e) => {
											e.stopPropagation();
											handleDelete();
										}}
									>
										<ImgIcon
											icon={trash}
											width={16}
											height={16}
											color={
												item.status === 'published'
													? 'rgba(255, 255, 255, 0.25)'
													: '#C71E1E'
											}
										/>
									</Button>
								</span>
								<span
									ref={renewTooltipAnchorRef}
									style={{ display: 'inline-flex' }}
									onMouseEnter={() => {
										setAdsTooltipKind('renew');
										setAdsTooltipOpen(true);
									}}
									onMouseLeave={() => setAdsTooltipOpen(false)}
								>
									<Button
										color={ButtonColors.Default}
										height={ButtonHeight.Height30}
										borderRadius={ButtonBorderRadius.Radius5}
										square={true}
										disabled={item.status === 'pending'}
										style={{
											padding: '0 10px',
											cursor: item.status === 'pending' ? 'default' : 'pointer',
										}}
										onClick={(e) => {
											if (item.status === 'pending') return;
											e.stopPropagation();
											handleRenew();
										}}
									>
										<ImgIcon
											icon={refresh}
											width={16}
											height={16}
											color={
												item.status === 'pending'
													? 'rgba(255, 255, 255, 0.25)'
													: '#ffffff'
											}
										/>
									</Button>
								</span>
							</StyledMyAdsButtonsContainer>
						</Fragment>
					) : (
						<Fragment>
							<StyledButtonsContainer>
								{/* <Button
								color={ButtonColors.Buy}
								height={ButtonHeight.Height30}
								borderRadius={ButtonBorderRadius.Radius5}
								disabled={!canCall}
								style={{
									padding: '0 10px',
									cursor: canCall ? 'pointer' : 'default',
								}}
								onClick={(e) => {
									if (!canCall) return;
									e.stopPropagation();
									handleCall();
								}}
							>
								<StyledButtonContent>
									<img src={call} style={{ opacity: canCall ? 1 : 0.25 }} />
									<Text>Позвонить</Text>
								</StyledButtonContent>
							</Button> */}
								<Button
									color={ButtonColors.Select2}
									height={ButtonHeight.Height30}
									borderRadius={ButtonBorderRadius.Radius5}
									disabled={!canCall}
									style={{
										padding: '0 10px',
										cursor: canCall ? 'pointer' : 'default',
									}}
									onClick={(e) => {
										if (!canCall) return;
										e.stopPropagation();
										handleMessage();
									}}
								>
									<StyledButtonContent>
										<img
											src={message}
											style={{ opacity: canCall ? 1 : 0.25 }}
										/>
										<Text>Написать</Text>
									</StyledButtonContent>
								</Button>
							</StyledButtonsContainer>
							<span
								ref={deleteFeedAnchorRef}
								style={{ display: 'inline-flex' }}
								onMouseEnter={() => {
									setAdsTooltipKind('delete-feed');
									setAdsTooltipOpen(true);
								}}
								onMouseLeave={() => setAdsTooltipOpen(false)}
							>
								<Button
									color={ButtonColors.Cancel25}
									height={ButtonHeight.Height30}
									borderRadius={ButtonBorderRadius.Radius5}
									square={true}
									disabled={!canCall && !item.isOwner && !isStaff}
									style={{ padding: '0 10px' }}
									onClick={(e) => {
										if (!item.isOwner && !isStaff) {
											return;
										}

										e.stopPropagation();
										handleDelete();
									}}
								>
									<ImgIcon
										icon={trash}
										width={16}
										height={16}
										color={
											item.isOwner || isStaff
												? '#C71E1E'
												: 'rgba(255, 255, 255, 0.25)'
										}
									/>
								</Button>
							</span>
						</Fragment>
					)}
				</StyledBottomContainer>
			</StyledContainer>
		</Fragment>
	);
};

export { AppAdsItem };
